import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * On-brand date picker. Coral-accented, JetBrains Mono weekday labels,
 * Fraunces month/year header, ink grid over paper background. Keyboard
 * + mouse friendly. F5.
 *
 * Props:
 *  - value: ISO date string (YYYY-MM-DD) or null
 *  - onChange: (iso | null) => void
 *  - minDaysAhead: number (default 14) — disables any date < today+14
 *  - defaultDate: ISO date (default = 1st of month, 60 days out) — used
 *    only when value is null and the panel first opens
 *  - testid: optional; rendered on the root wrapper
 *
 * Design notes:
 *  - Values are stored/emitted as YYYY-MM-DD (date-only, no time component)
 *    so there's no timezone wobble between client and server.
 *  - Tap targets are 40px minimum per F5 spec.
 *  - We render a headless input + a popover panel; no external deps.
 */
const DOW_EN = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const DOW_ES = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];
const MONTHS_EN = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const MONTHS_ES = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function pad(n) {
    return String(n).padStart(2, "0");
}

function toISO(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fromISO(iso) {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

function addDays(d, n) {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

function sameDay(a, b) {
    return (
        a &&
        b &&
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

export default function LagunaDatePicker({
    value,
    onChange,
    minDaysAhead = 14,
    defaultDate,
    locale = "en",
    placeholder,
    testid,
    disabled = false,
}) {
    const today = useMemo(() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t;
    }, []);
    const minDate = useMemo(() => addDays(today, minDaysAhead), [today, minDaysAhead]);

    const initialDefault = useMemo(() => {
        if (defaultDate) return fromISO(defaultDate) || minDate;
        // 1st of month, 60 days out.
        const anchor = addDays(today, 60);
        const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        return first >= minDate ? first : minDate;
    }, [defaultDate, today, minDate]);

    const [open, setOpen] = useState(false);
    const [view, setView] = useState(
        startOfMonth(fromISO(value) || initialDefault),
    );
    const rootRef = useRef(null);

    // Close on outside click / Escape
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // When value changes externally, re-center the view
    useEffect(() => {
        if (value) {
            const d = fromISO(value);
            if (d) setView(startOfMonth(d));
        }
    }, [value]);

    const months = locale.startsWith("es") ? MONTHS_ES : MONTHS_EN;
    const dow = locale.startsWith("es") ? DOW_ES : DOW_EN;
    const selectedDate = fromISO(value);

    // Build month grid — 6 rows × 7 cols, Mon-first weeks
    const grid = useMemo(() => {
        const first = startOfMonth(view);
        // Monday-first: JS getDay() is Sun=0..Sat=6; shift so Mon=0..Sun=6.
        const offset = (first.getDay() + 6) % 7;
        const start = addDays(first, -offset);
        const cells = [];
        for (let i = 0; i < 42; i += 1) cells.push(addDays(start, i));
        return cells;
    }, [view]);

    const formatLong = (d) => {
        if (!d) return "";
        if (locale.startsWith("es")) {
            return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
        }
        return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    };

    const prevDisabled = view <= startOfMonth(minDate);
    const nextDisabled = false;

    const pick = (d) => {
        if (d < minDate) return;
        onChange && onChange(toISO(d));
        setOpen(false);
    };

    return (
        <div
            className={`lg-dp ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`}
            ref={rootRef}
            data-testid={testid}
        >
            <button
                type="button"
                className="lg-dp-trigger"
                onClick={() => !disabled && setOpen((v) => !v)}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={placeholder || "Pick a date"}
                disabled={disabled}
                data-testid={testid ? `${testid}-trigger` : undefined}
            >
                <span className={`lg-dp-value ${selectedDate ? "" : "lg-dp-placeholder"}`}>
                    {selectedDate ? formatLong(selectedDate) : (placeholder || "Select a date")}
                </span>
                <span className="lg-dp-chevron" aria-hidden="true" />
            </button>
            {open && (
                <div className="lg-dp-panel" role="dialog" aria-label="Calendar">
                    <div className="lg-dp-head">
                        <button
                            type="button"
                            className="lg-dp-nav"
                            onClick={() =>
                                setView(
                                    new Date(view.getFullYear(), view.getMonth() - 1, 1),
                                )
                            }
                            disabled={prevDisabled}
                            aria-label="Previous month"
                        >
                            ‹
                        </button>
                        <div className="lg-dp-title">
                            <span className="lg-dp-month">{months[view.getMonth()]}</span>{" "}
                            <span className="lg-dp-year">{view.getFullYear()}</span>
                        </div>
                        <button
                            type="button"
                            className="lg-dp-nav"
                            onClick={() =>
                                setView(
                                    new Date(view.getFullYear(), view.getMonth() + 1, 1),
                                )
                            }
                            disabled={nextDisabled}
                            aria-label="Next month"
                        >
                            ›
                        </button>
                    </div>
                    <div className="lg-dp-dow">
                        {dow.map((w) => (
                            <span key={w} className="lg-dp-dow-cell">{w}</span>
                        ))}
                    </div>
                    <div className="lg-dp-grid">
                        {grid.map((d, i) => {
                            const outside = d.getMonth() !== view.getMonth();
                            const disabled = d < minDate;
                            const selected = sameDay(d, selectedDate);
                            const isToday = sameDay(d, today);
                            const cls = [
                                "lg-dp-cell",
                                outside ? "is-outside" : "",
                                disabled ? "is-disabled" : "",
                                selected ? "is-selected" : "",
                                isToday ? "is-today" : "",
                            ]
                                .filter(Boolean)
                                .join(" ");
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    className={cls}
                                    onClick={() => pick(d)}
                                    disabled={disabled}
                                    aria-pressed={selected}
                                    aria-label={formatLong(d)}
                                >
                                    {d.getDate()}
                                </button>
                            );
                        })}
                    </div>
                    <div className="lg-dp-foot">
                        <span className="lg-dp-min">
                            {locale.startsWith("es")
                                ? `Mínimo ${minDaysAhead} días desde hoy`
                                : `Min. ${minDaysAhead} days from today`}
                        </span>
                        {selectedDate && (
                            <button
                                type="button"
                                className="lg-dp-clear"
                                onClick={() => {
                                    onChange && onChange(null);
                                    setOpen(false);
                                }}
                            >
                                {locale.startsWith("es") ? "Borrar" : "Clear"}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
